<template>
  <div class="tennisracket-tool__container">
    <div v-if="!step" class="tennisracket-tool__placeholder" :style="placeholderStyle">
      <div class="tennisracket-tool__label">{{ t('racket.find_perfect') }}</div>
      <button
        type="button"
        class="button button--primary button--collapsed tennisracket-tool__button--start"
        @click="step = 1"
      >
        {{ t('racket.start_tool') }}
      </button>
    </div>
    <div v-else class="tennisracket-tool__selection">
      <div class="tennisracket-tool__steps_row">
        <div
          v-for="(stepDef, index) in steps"
          :key="stepDef.stepKey"
          class="tennisracket-tool__step"
          :class="{ 'tennisracket-tool__step--active': step > index }"
          @click="goTo(index + 1)"
        >
          {{ t(stepDef.stepKey) }}
        </div>
      </div>
      <div class="options_row">
        <div v-for="(stepDef, index) in steps" v-show="step === index + 1" :key="stepDef.titleKey">
          <h3>{{ index + 1 }}. {{ t(stepDef.titleKey) }}</h3>
          <template v-if="stepDef.choices">
            <div
              v-for="choice in stepDef.choices"
              :key="choice.key"
              class="tennisracket-tool__choice_container"
              @click="
                setValue(
                  index + 1,
                  stepDef.titleKey,
                  choice.titleKey,
                  choice.key,
                  stepDef.attribute
                )
              "
            >
              <img class="tennisracket-tool__choice_image" :src="choice.image" alt="" />
              <span class="tennisracket-tool__choice_title">{{ t(choice.titleKey) }}</span>
            </div>
          </template>
          <template v-if="stepDef.input">
            <div class="tennisracket-tool__choice">
              <img
                v-if="stepDef.input.image"
                class="tennisracket-tool__choice_image"
                :src="stepDef.input.image"
                alt=""
              />
              <div class="tennisracket-tool__form">
                <label :for="stepDef.input.titleKey" class="form__label">
                  {{ t(stepDef.input.titleKey) }}
                </label>
                <input
                  :id="stepDef.input.titleKey"
                  v-model="inputValues[stepDef.attribute ?? '']"
                  type="number"
                  class="form__input form__input--text"
                  :min="stepDef.input.min"
                  :max="stepDef.input.max"
                  required
                />
                <button
                  v-if="stepDef.nextButton"
                  type="button"
                  class="button button--secondary button--small tennisracket-tool__submit"
                  @click="setInputValue(index + 1, stepDef.input.titleKey, stepDef.attribute)"
                >
                  {{ t('step.next') }}
                </button>
              </div>
            </div>
          </template>
          <template v-if="stepDef.summary">
            <div
              v-for="(selected, stepIndex) in selectedValues"
              :key="stepIndex"
              class="tennisracket-tool__summary-row"
            >
              <div class="tennisracket-tool__summary-key">{{ selected.label }}</div>
              <div class="tennisracket-tool__summary-value">
                {{ selected.valueLabel || selected.value }}
              </div>
              <div class="tennisracket-tool__summary-back" @click="goTo(Number(stepIndex))">
                {{ t('step.edit') }}
              </div>
            </div>
            <button
              type="button"
              class="button button--secondary tennisracket-tool__button--results"
              :disabled="loading"
              @click="showResults"
            >
              {{ t('step.result') }}
            </button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import axios from 'axios';
import { computed, reactive, ref } from 'vue';

import { generateUrl } from '@shop/utils/Routing';
import { useTranslation } from '@shop/utils/Translation';

import type {
  TennisRacketSelectedValue,
  TennisRacketToolBlockProps,
} from './TennisRacketToolBlock.types';

const props = defineProps<TennisRacketToolBlockProps>();

const { t } = useTranslation('tennisracket_tool');

const placeholderStyle = computed(() =>
  props.backgroundImage ? { backgroundImage: `url('${props.backgroundImage}')` } : {}
);

const translate = (key: string): string => t.value(key);

const step = ref(0);
const loading = ref(false);
const inputValues = reactive<Record<string, string>>({});
const selectedValues = reactive<Record<number, TennisRacketSelectedValue>>({});

const setValue = (
  index: number,
  labelKey: string,
  valueLabelKey: string,
  value: string,
  attribute?: string
): void => {
  selectedValues[index] = {
    label: translate(labelKey),
    valueLabel: translate(valueLabelKey),
    value,
    attribute,
  };
  step.value += 1;
};

const setInputValue = (index: number, labelKey: string, attribute?: string): void => {
  if (!attribute) {
    return;
  }
  selectedValues[index] = {
    label: translate(labelKey),
    attribute,
    value: inputValues[attribute] ?? '',
  };
  step.value += 1;
};

const goTo = (targetStep: number): void => {
  step.value = targetStep;
};

const showResults = async (): Promise<void> => {
  loading.value = true;

  const params: Record<string, string> = {
    url_path: window.location.pathname,
  };

  Object.values(selectedValues).forEach(selected => {
    if (selected.attribute) {
      params[selected.attribute] = selected.value;
    }
  });

  try {
    const result = await axios.get<{ url?: string }>(
      generateUrl('category_tool_tennisracket_url', params)
    );
    if (result.data.url) {
      window.location.href = result.data.url;
    }
  } finally {
    loading.value = false;
  }
};

const steps = props.steps;
</script>
